import { type FieldValues, useController } from 'react-hook-form';
import { Textarea } from '../../components/ui';
import { cn } from '../../utils/cn.js';

interface BuilderTextareaFieldProps<TValues extends FieldValues> {
  /** Path of the value in the surrounding form. */
  name: Parameters<typeof useController<TValues>>[0]['name'];
  'aria-label': string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * A form-bound text area that fills the space its parent gives it (monospace editors in the step panels).
 * The form binders in `components/form` size a text area by rows; an editor that must grow with the panel uses
 * this, with the same validation: the field's message shows under it and the border turns to the error colour.
 */
export function BuilderTextareaField<TValues extends FieldValues>({
  name,
  placeholder,
  className,
  disabled,
  'aria-label': ariaLabel,
}: BuilderTextareaFieldProps<TValues>) {
  const { field, fieldState } = useController<TValues>({ name });
  const message = fieldState.error?.message;
  return (
    <>
      <Textarea
        ref={field.ref}
        name={field.name}
        aria-label={ariaLabel}
        value={typeof field.value === 'string' ? field.value : ''}
        onChange={(event) => field.onChange(event.target.value)}
        onBlur={field.onBlur}
        invalid={fieldState.invalid}
        disabled={disabled || field.disabled}
        placeholder={placeholder}
        className={cn('min-h-[60px] flex-1 font-mono text-xs', className)}
      />
      {message && (
        <p role="alert" className="mt-1 shrink-0 text-xs text-danger">
          {message}
        </p>
      )}
    </>
  );
}
